/**
 * TemplateSkeleton — a tiny layout thumbnail for the template picker. Pure
 * placeholder blocks (no real data) that hint at each template's structure so a
 * user can tell them apart at a glance: Classic (single column, bullet section
 * titles), Minimal (airy, thin underlined headings), Split (two-column intro).
 */

const Bar = ({ w, strong }: { w: string; strong?: boolean }) => (
  <div
    className={`h-1 rounded-sm ${strong ? "bg-gray-3" : "bg-gray-2"}`}
    style={{ width: w }}
  />
);

const Classic = () => (
  <div className="flex h-full flex-col gap-1.5 p-2">
    <div className="flex items-center gap-1.5">
      <div className="h-4 w-4 shrink-0 rounded-full bg-gray-3" />
      <div className="flex flex-col gap-1">
        <div className="h-1.5 w-10 rounded-sm bg-gray-4" />
        <Bar w="1.75rem" />
      </div>
    </div>
    <div className="mt-0.5 flex items-center gap-1">
      <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-op-blue/70" />
      <div className="h-1.5 w-12 rounded-sm bg-gray-3" />
    </div>
    <div className="flex flex-col gap-1 pl-2.5">
      <Bar w="100%" />
      <Bar w="90%" />
      <Bar w="70%" />
    </div>
    <div className="mt-0.5 flex items-center gap-1">
      <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-op-blue/70" />
      <div className="h-1.5 w-9 rounded-sm bg-gray-3" />
    </div>
    <div className="flex flex-col gap-1 pl-2.5">
      <Bar w="100%" />
      <Bar w="80%" />
    </div>
  </div>
);

const Minimal = () => (
  <div className="flex h-full flex-col gap-2 p-2">
    <div className="flex flex-col gap-1 pt-0.5">
      <div className="h-1.5 w-10 rounded-sm bg-gray-4" />
      <Bar w="1.5rem" />
    </div>
    <div className="mt-0.5 flex flex-col gap-1">
      <Bar w="2rem" strong />
      <div className="h-px w-full bg-gray-3" />
    </div>
    <div className="flex flex-col gap-1.5">
      <Bar w="100%" />
      <Bar w="80%" />
    </div>
    <div className="mt-0.5 flex flex-col gap-1">
      <Bar w="1.75rem" strong />
      <div className="h-px w-full bg-gray-3" />
    </div>
    <div className="flex flex-col gap-1.5">
      <Bar w="100%" />
      <Bar w="65%" />
    </div>
  </div>
);

const Split = () => (
  <div className="flex h-full flex-col gap-1.5 p-2">
    <div className="flex gap-1.5">
      <div className="flex w-[34%] shrink-0 flex-col items-center gap-1 rounded-sm bg-gray-1 p-1">
        <div className="h-5 w-5 rounded-full bg-gray-3" />
        <Bar w="100%" />
        <Bar w="85%" />
        <Bar w="70%" />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="h-1.5 w-10 rounded-sm bg-gray-4" />
        <Bar w="1.5rem" />
        <div className="mt-0.5 flex flex-col gap-1">
          <Bar w="100%" />
          <Bar w="100%" />
          <Bar w="60%" />
        </div>
      </div>
    </div>
    <div className="mt-0.5 flex flex-col gap-1">
      <Bar w="2.5rem" strong />
      <div className="h-px w-full bg-op-blue/50" />
    </div>
    <div className="flex flex-col gap-1">
      <Bar w="100%" />
      <Bar w="85%" />
    </div>
  </div>
);

const SKELETONS: Record<string, () => JSX.Element> = {
  classic: Classic,
  minimal: Minimal,
  split: Split,
};

export default function TemplateSkeleton({ id }: { id: string }) {
  const Skeleton = SKELETONS[id] ?? Classic;
  return (
    <div className="aspect-[3/4] w-full overflow-hidden rounded-md bg-white">
      <Skeleton />
    </div>
  );
}
